import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  FormControlLabel,
  Checkbox,
  Grid,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, Email } from "@mui/icons-material";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";

import { login as loginAPI } from "./services";
import {

 
  setUserDetails,
} from "./Userslice";
import { login } from "../../config/images";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Fix: Change state.user to state.userState to match the rootReducer structure
  const {  error, isAuthenticated } = useSelector(
    (state) => state.userState
  );
  const [loading, setLoading] = useState(false);
  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Handle login form changes
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Validation
  const validateLoginForm = () => {
    const newErrors = {};

    if (!loginForm.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(loginForm.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!loginForm.password) {
      newErrors.password = "Password is required";
    } else if (loginForm.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Login handler
  const handleLoginSubmit = async () => {
    if (!validateLoginForm()) {
      return;
    }
    setLoading(true)
    try {
      const response = await loginAPI({
        email: loginForm.email,
        password: loginForm.password,
      });

      if (response.data.success == true) {
        setLoading(false)
        dispatch(
          setUserDetails({
            token: response.data.payload.access_token,
            userName: response.data.payload.user.first_name,
            sessionTimeout: response.data.payload.user.session_timeout, // Add this line
          })
        );
        toast.success(response.data.message,{toastId:"login-success"});
        navigate("/dashboard");
      } else {
        setLoading(false)
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      setLoading(false)
      console.error("Login error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Login failed. Please check your credentials.";

      toast.error(errorMessage,{toastId:"login-error"});
    }
  };

  // Effects
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);



  // Render functions
  const renderLoginForm = () => (
    <Box
      sx={{
        width: {md:400},
        maxWidth: 600, // Increased from 600 to 800
        backgroundColor: "#FFFFFF",
        padding: "25px",
        borderRadius: "8px",
        mx: "auto",
        boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: "bold", mb: 4, textAlign: "center" }}
      >
        Admin Login
      </Typography>

      {error && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "#ffebee",
            borderRadius: "4px",
            border: "1px solid #f44336",
          }}
        >
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      )}

      <Stack>
        <Typography
          variant="body2"
          sx={{ fontWeight: "600", textAlign: "left", color: "#333333" }}
        >
          Email
        </Typography>
        <TextField
          fullWidth
          placeholder="admin@example.com"
          variant="outlined"
          name="email"
          value={loginForm.email}
          onChange={handleLoginChange}
          helperText={
            errors.email && (
              <FormHelperText
                sx={{ color: "red", marginLeft: 0, marginRight: 0 }}
              >
                {errors.email}
              </FormHelperText>
            )
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" sx={{ color: "#1976d2" }}>
                  <Email />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiFormHelperText-root": {
              marginLeft: 0,
              marginRight: 0,
              color: "red",
            },
            "& .MuiOutlinedInput-root": {
              boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
              borderRadius: "8px",
            },
            "& .MuiOutlinedInput-input": {
              padding: "10px",
              "&::placeholder": {
                fontSize: "14px",
              },
            },
          }}
        />

        <Typography
          variant="body2"
          sx={{ fontWeight: "600", textAlign: "left", color: "#333333" }}
        >
          Password
        </Typography>
        <TextField
          fullWidth
          placeholder="Password"
          variant="outlined"
          name="password"
          type={showPassword ? "text" : "password"}
          value={loginForm.password}
          onChange={handleLoginChange}
          helperText={
            errors.password && (
              <FormHelperText
                sx={{ color: "red", marginLeft: 0, marginRight: 0 }}
              >
                {errors.password}
              </FormHelperText>
            )
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  sx={{ color: "#1976d2" }}
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiFormHelperText-root": {
              marginLeft: 0,
              marginRight: 0,
              color: "red",
            },
            "& .MuiOutlinedInput-root": {
              boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
              borderRadius: "8px",
            },
            "& .MuiOutlinedInput-input": {
              padding: "10px",
              "&::placeholder": {
                fontSize: "14px",
              },
            },
          }}
        />
      </Stack>

      <Button
        fullWidth
        variant="contained"
        disabled={loading}
        sx={{
          bgcolor: "#1570EF",
          color: "white",
          textTransform: "capitalize",
          fontSize: "16px",
          borderRadius: "6px",
          py: 1.5,
          mt: 2,
          "&:hover": { bgcolor: "#1570EF" },
          "&:disabled": { bgcolor: "#ccc" },
        }}
        onClick={handleLoginSubmit}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
      </Button>
    </Box>
  );

  return (
    <>
  
    <Grid
      container
      minHeight="100vh"
      bgcolor="#f5faff"
      style={{ textAlign: "center" }}
    >
      <Grid
        item
        xs={false}
        md={6}
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <img src={login.login} alt="Illustration" />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 4 },
          marginLeft: "auto",
          marginRight: "auto",
          alignItems: "center" /* Add this for vertical centering */,
          justifyContent: "center" /* Add this for vertical centering */,
        }}
      >
        <Box
          sx={{
            justifyContent: "center",
            mb: 1,
            mt: 0 /* Change this to 0 */,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              textAlign: "center",
            }}
          >
            <img
              src={login.logo}
              alt="Logo"
              style={{ width: "250px", cursor: "pointer" }}
            />
          </Box>
        </Box>
        {renderLoginForm()}
      </Grid>
    </Grid>
    </>
  );
};

export default Login;
